import { useQuery } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.gen';

export type Message = components['schemas']['Message'];
export type MessagePage = components['schemas']['MessagePage'];
export type MessageStatus = components['schemas']['MessageStatus'];
export type Channel = components['schemas']['Channel'];

export interface MessagesQueryParams {
  page: number;
  pageSize: number;
  status?: MessageStatus;
  channel?: Channel;
  customerId?: string;
  /** ISO 8601 (включая TZ). */
  from?: string;
  to?: string;
}

function buildMessagesUrl(params: MessagesQueryParams): string {
  const sp = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.pageSize),
  });
  if (params.status) sp.set('status', params.status);
  if (params.channel) sp.set('channel', params.channel);
  if (params.customerId) sp.set('customer_id', params.customerId);
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  return `/api/v1/admin/messages?${sp.toString()}`;
}

export function useMessagesQuery(params: MessagesQueryParams) {
  return useQuery({
    queryKey: ['messages', params],
    queryFn: () => api.get<MessagePage>(buildMessagesUrl(params)),
    placeholderData: (prev) => prev,
  });
}

export function useMessageStatusQuery(messageId: string | null, enabled: boolean) {
  return useQuery({
    enabled: Boolean(messageId) && enabled,
    queryKey: ['messages', 'detail', messageId],
    queryFn: () => api.get<Message>(`/api/v1/admin/messages/${messageId}`),
    refetchInterval: (query) => {
      const data = query.state.data as Message | undefined;
      if (!data) return 1500;
      if (data.status === 'queued' || data.status === 'sending') return 1500;
      return false;
    },
    refetchIntervalInBackground: false,
  });
}
